import React, { useState } from 'react';
import axios from 'axios';
import { Ticket, X, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const CouponApply = ({ appointmentId, clientId, orderTotal, appliedCoupon, onCouponApplied, onCouponRemoved, showToast }) => {
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState('');

  const handleValidate = async () => {
    if (!code.trim()) return;
    setValidating(true);
    setError('');
    setValidationResult(null);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: code.trim(),
        order_total: orderTotal || 0,
        client_id: clientId
      });
      setValidationResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setValidating(false);
    }
  };

  const handleApply = async () => {
    if (!validationResult || !appointmentId) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/coupons/apply`, {
        appointment_id: appointmentId,
        code: validationResult.code
      });
      onCouponApplied?.({
        code: validationResult.code,
        discount: res.data.discount,
        new_total: res.data.new_total,
        discount_type: validationResult.discount_type,
        discount_value: validationResult.discount_value
      });
      showToast?.(`Coupon ${validationResult.code} applied! Saved $${res.data.discount.toFixed(2)}`, 'success');
      setCode('');
      setValidationResult(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to apply coupon');
    } finally {
      setApplying(false);
    }
  };

  const handleRemove = async () => {
    if (!appointmentId) return;
    try {
      const res = await axios.post(`${API}/coupons/remove`, { appointment_id: appointmentId });
      onCouponRemoved?.(res.data.restored_total);
      showToast?.('Coupon removed', 'success');
    } catch (err) {
      showToast?.('Failed to remove coupon', 'error');
    }
  };

  if (appliedCoupon) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="coupon-applied">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
              <Check size={14} className="text-green-600" />
            </div>
            <div>
              <span className="text-sm font-semibold text-green-800 font-mono">{appliedCoupon.code || appliedCoupon}</span>
              <span className="text-xs text-green-600 ml-2">
                -${(appliedCoupon.discount || appliedCoupon.coupon_discount || 0).toFixed(2)} off
              </span>
            </div>
          </div>
          <button
            data-testid="coupon-remove-btn"
            onClick={handleRemove}
            className="text-green-600 hover:text-red-500 transition-colors p-1"
            title="Remove coupon"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="coupon-apply-section">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Ticket size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="coupon-code-field"
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); setValidationResult(null); }}
            placeholder="Coupon code"
            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleValidate(); } }}
          />
        </div>
        <button
          data-testid="coupon-apply-btn"
          type="button"
          onClick={validationResult ? handleApply : handleValidate}
          disabled={!code.trim() || validating || applying}
          className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
        >
          {(validating || applying) && <Loader2 size={14} className="animate-spin" />}
          {validating ? 'Checking...' : applying ? 'Applying...' : validationResult ? 'Apply' : 'Validate'}
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1" data-testid="coupon-error">
          <X size={12} /> {error}
        </p>
      )}

      {validationResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm" data-testid="coupon-preview">
          <div className="flex justify-between items-center">
            <span className="text-blue-800 font-medium">
              {validationResult.discount_type === 'percentage'
                ? `${validationResult.discount_value}% off`
                : `$${validationResult.discount_value.toFixed(2)} off`}
            </span>
            <span className="text-blue-900 font-bold">-${validationResult.calculated_discount.toFixed(2)}</span>
          </div>
          {validationResult.description && (
            <p className="text-xs text-blue-600 mt-1">{validationResult.description}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CouponApply;
